import { AlertTriangle, ArrowRightLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import type { ContactTransfer } from "@/types/contactDeletion";

interface Props {
  conflict: ContactTransfer;
  onTransfer: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

/**
 * Shown when a contact's CRM sync failed because the record already exists there.
 *
 * The failure is not fixable by retrying — the CRM will keep rejecting it — so the
 * banner offers the two things that do resolve it: move this contact's history onto
 * the record the CRM kept, or delete this one. Transfer is presented first because
 * deleting without it silently destroys the notes and diary entries.
 */
export function DuplicateConflictBanner({ conflict, onTransfer, onDelete, disabled }: Props) {
  const target = conflict.target;

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            This contact already exists in the CRM
          </p>
          <p className="mt-0.5 text-sm text-amber-800">
            The CRM rejected it as a duplicate of{" "}
            <span className="font-semibold">
              {target ? `${target.name?.trim() || "(unnamed)"} · CRM ID ${target.externalId}` : `CRM ID ${conflict.duplicateExternalId}`}
            </span>
            . Syncing will keep failing until this is resolved.
          </p>

          {conflict.ambiguous && (
            <p className="mt-1.5 text-sm text-amber-800">
              More than one contact here carries that CRM ID — pick the right one when transferring.
            </p>
          )}

          {!target && !conflict.ambiguous && (
            <p className="mt-1.5 text-sm text-amber-800">
              That record has not synced into Atlas yet, so there is nothing to transfer onto. You
              can still search for another contact, or delete this one.
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={onTransfer} disabled={disabled}>
              <ArrowRightLeft size={14} /> Transfer notes &amp; diary
            </Button>
            <Button variant="danger" size="sm" onClick={onDelete} disabled={disabled}>
              <Trash2 size={14} /> Delete this contact
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
